// ---------------- Component for the model prediction panel ------------------------
// ----------------------------------------------------------------------------------

// import all the components and hooks we need
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from './Sidebar';
import { useResearch } from '../hooks/useResearch';
import './ResearchPage.css';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

// ----------------------------------------------------------------------------------

// configuration for the suggested questions, source labels, welcome content, year options, and model options
const SUGGESTED_QUESTIONS = [
  { label: 'Hybrid vs SPY', q: 'How did the Hybrid model perform compared to SPY across 2022–2025?' },
  { label: 'Best Sharpe Ratio', q: 'Which model achieved the best Sharpe Ratio and in which year?' },
  { label: 'XGBoost drivers', q: 'What are the top features driving buy signals for XGBoost?' },
  { label: 'Macro environment', q: 'What does the macroeconomic environment suggest about current market conditions?' },
];

const SOURCE_LABELS = { // used to display the source of the information used in the AI assistant's response
  macro: { label: 'Macro', color: '#06B6D4' },
  news: { label: 'News', color: '#8B5CF6' },
  backtest: { label: 'Backtests', color: '#22C55E' },
  feature_importance: { label: 'SHAP', color: '#F59E0B' },
  google_trends: { label: 'Google Trends', color: '#EC4899' },
};

// fills the welcome message to show the user what the AI assistant can do and what data it has access to
const WELCOME_CONTENT = `Hello! I'm your AI research assistant, grounded in this dissertation's data.

**I have access to:**
- Stock Prices and Macroeconomic indicators (FRED)
- Financial news and Google Trends
- Backtest results for all 4 models (2022–2025)
- SHAP feature importance for signals

Select a **model**, **timeframe**, and **symbol**, then ask a question.`;

const YEAR_OPTIONS = [ // used to display the year options in the dropdown for the user to select
  { label: 'All Years', value: 'MAX' },
  ...Array.from({ length: 11 }, (_, i) => {
    const y = String(2025 - i);
    return { label: y, value: y };
  }),
];

const MODEL_OPTIONS = [ // used to display the model options in the dropdown for the user to select
  { label: 'All Models', value: null },
  { label: 'Hybrid', value: 'hybrid' },
  { label: 'XGBoost', value: 'xgb' },
  { label: 'LogReg', value: 'logreg' },
  { label: 'MLP', value: 'mlp' },
  { label: 'TabNet', value: 'tabnet' },
  { label: 'FT-Transformer', value: 'fttransformer' }
];

// ----------------------------------------------------------------------------------
// main component for the research page

const ResearchPage = () => {
  // set the state variables for the messages, input, selected year, and selected model
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME_CONTENT, contextUsed: [] },
  ]);
  const [input, setInput] = useState('');
  const [selectedYear, setSelectedYear] = useState('MAX');
  const [selectedModel, setSelectedModel] = useState(null);

  const { usage, loading, error, sendQuery } = useResearch();
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); // scroll to the bottom of the chat when a new message is added
  }, [messages, loading]);

  // function to handle sending a question to the AI assistant, either from the input or from a suggested question
  const handleSend = async (questionOverride) => {
    const question = (questionOverride || input).trim(); // get the question from the input or the suggested button
    if (!question || loading) return; // if the question is empty or the AI assistant is still processing a previous request, do nothing

    setInput(''); // clear the input field
    setMessages(prev => [...prev, { role: 'user', content: question }]); // add the user's question to the messages state

    try {
      const data = await sendQuery({ question, timeframe: selectedYear, model: selectedModel }); // send the question to the AI assistant and get the response
      setMessages(prev => [ // set the messages state to include the AI assistant's response, along with any context used in generating the response
        ...prev,
        { role: 'assistant', content: data.answer, contextUsed: data.context_used || [] },
      ]);
    } catch { // if there is an error processing the request, add an error message to the messages state
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Error processing request.', isError: true, contextUsed: [] },
      ]);
    }
  };

  // function to handle pressing the Enter key in the input field, which will send the question if Shift is not held down
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => { // function to clear the chat and reset the messages state to the welcome message
    setMessages([{ role: 'assistant', content: WELCOME_CONTENT, contextUsed: [] }]);
  };

  // now return the JSX to render the page
  return (
    <div className="research-layout">
      <Sidebar selectedStock="AAPL" onSelectStock={() => {}} timeframe="MAX" />

      <div className="research-main">
        <header className="research-header">

          {/* add the title and subtitle */}
          <div className="research-header-left">
            <h1 className="research-title">
              AI Research Assistant
            </h1>
            <span className="research-subtitle">Grounded in dissertation data & market indicators</span>
          </div>

          {/* start the controls on the right side of the header */}
          <div className="research-controls">
            <div className="research-selectors">

              {/* create the drop down for the year options */}
              <div className="research-selector-group">
                <label>Year</label>
                <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} disabled={loading}> 
                  {YEAR_OPTIONS.map(opt => ( // loop through the year options and create a dropdown option for each one
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* create the drop down for the model options */}
              <div className="research-selector-group">
                <label>Model</label>
                <select value={selectedModel ?? ''} onChange={e => setSelectedModel(e.target.value || null)} disabled={loading}>
                  {MODEL_OPTIONS.map(opt => ( // loop through the model options and create a dropdown option for each one
                    <option key={opt.value ?? ''} value={opt.value ?? ''}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* display the usage bar and remaining requests */}
            <div className={`usage-counter ${usage.remaining <= 5 ? 'low' : ''}`}>
              <div className="usage-bar">
                <div className="usage-bar-fill" style={{ width: `${(usage.requests_today / usage.daily_limit) * 100}%` }} />
              </div>
              <span className="usage-label">{usage.remaining} left today</span>
            </div>

            {/* add the button to clear the chat */}
            <button className="research-clear-btn" onClick={handleClear}>
              New Chat
            </button>
          </div>
        </header>

        {/* give a section to the user to show suggested questions that they can click on to ask the AI assistant */}
        <div className="research-suggestions">
          <div className="suggestions-scroll">
            {SUGGESTED_QUESTIONS.map((item, i) => ( // loop through the suggested questions and create a button for each one
              <button key={i} className="suggestion-chip" onClick={() => handleSend(item.q)} disabled={loading}>
                {item.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* create the main chat area where the messages will be displayed */}
        <div className="research-chat">
          <div className="research-messages">
            {messages.map((msg, i) => ( // loop through the messages and display each one in the chat area
              <div key={i} className={`research-message ${msg.role} ${msg.isError ? 'error' : ''}`}>
                <div className="message-bubble">
                  <div className="message-markdown">
                    <ReactMarkdown plugins={[gfm]}>{msg.content}</ReactMarkdown>
                  </div>

                  {msg.contextUsed?.length > 0 && ( // if the message has used context sources
                    <div className="context-badges">
                      <span className="context-badges-label">Sources:</span>
                      {msg.contextUsed.map((src, j) => { // loop through the context sources and display each one as a badge with the correct label and color
                        const srcKey = Object.keys(SOURCE_LABELS).find(k => src.startsWith(k)) || src;
                        const meta = SOURCE_LABELS[srcKey];
                        const label = src.startsWith('stock_') ? src.replace('stock_', '') : (meta?.label || src);
                        return (
                          <span key={j} className="context-badge" style={{ '--badge-color': meta?.color || '#6B7280' }}>
                            {label.toUpperCase()}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && ( // if the AI assistant is still processing a request, show a loading message in the chat area
              <div className="research-message assistant">
                <div className="message-bubble loading-bubble">
                  <div className="typing-indicator"><span /><span /><span /></div>
                  <span className="loading-label">Briefing the AI agents on their mission...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* create the input area where the user can type their question and send it to the AI assistant */}
          <div className="research-input-area">
            {/* if there is an error show that we are unable to process the request */}
            {error && <div className="research-error">{error}</div>} 
            <div className="research-input-row">
              <textarea
                className="research-input"
                placeholder="Ask a question..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={loading}
              />
              <button 
                className="research-send-btn" 
                onClick={() => handleSend()} 
                disabled={loading || !input.trim() || usage.remaining === 0}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchPage;